import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Add1Icon from 'zignal-landscape/lib/Icon/Add1Icon';
import AlignLeftIcon from 'zignal-landscape/lib/Icon/AlignLeftIcon';
import AnalyzeIcon from 'zignal-landscape/lib/Icon/AnalyzeIcon';
import ChartBarIcon from 'zignal-landscape/lib/Icon/ChartBarIcon';
import ChartBarStackedIcon from 'zignal-landscape/lib/Icon/ChartBarStackedIcon';
import ChartDonutIcon from 'zignal-landscape/lib/Icon/ChartDonutIcon';
import ChartIcon from 'zignal-landscape/lib/Icon/ChartIcon';
import ChartNumbersIcon from 'zignal-landscape/lib/Icon/ChartNumbersIcon';
import CloudIcon from 'zignal-landscape/lib/Icon/CloudIcon';
import CompetitionIcon from 'zignal-landscape/lib/Icon/CompetitionIcon';
import EditIcon from 'zignal-landscape/lib/Icon/EditIcon';
import EmojiIcon from 'zignal-landscape/lib/Icon/EmojiIcon';
import GlobeIcon from 'zignal-landscape/lib/Icon/GlobeIcon';
import IFrameIcon from 'zignal-landscape/lib/Icon/IFrameIcon';
import ListIcon from 'zignal-landscape/lib/Icon/ListIcon';
import Minus1Icon from 'zignal-landscape/lib/Icon/Minus1Icon';
import PdfIcon from 'zignal-landscape/lib/Icon/PdfIcon';
import PeakIcon from 'zignal-landscape/lib/Icon/PeakIcon';
import ReadingListIcon from 'zignal-landscape/lib/Icon/ReadingListIcon';
import SpideyIcon from 'zignal-landscape/lib/Icon/SpideyIcon';
import SwfIcon from 'zignal-landscape/lib/Icon/SwfIcon';
import TrashIcon from 'zignal-landscape/lib/Icon/TrashIcon';

import styles from './CategoryItem.css';

const cx = classNames.bind(styles);

const iconMap = {
  AlignLeft: AlignLeftIcon,
  Analyze: AnalyzeIcon,
  Chart: ChartIcon,
  ChartBar: ChartBarIcon,
  ChartBarStacked: ChartBarStackedIcon,
  ChartDonut: ChartDonutIcon,
  ChartNumbers: ChartNumbersIcon,
  Cloud: CloudIcon,
  Competition: CompetitionIcon,
  Emoji: EmojiIcon,
  Globe: GlobeIcon,
  IFrame: IFrameIcon,
  List: ListIcon,
  PDF: PdfIcon,
  Peak: PeakIcon,
  ReadingList: ReadingListIcon,
  SWF: SwfIcon,
  Spidey: SpideyIcon
};

export default class CategoryItem extends React.Component {
  constructor(props) {
    super(props);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  handleKeyPress(e) {
    let propagate = false;
    switch (e.key) {
      case 'ArrowRight':
        this.props.increment();
        break;
      case 'ArrowLeft':
        this.props.decrement();
        break;
      case 'ArrowUp':
        this.navigateUp();
        break;
      case 'ArrowDown':
        this.navigateDown();
        break;
      case 'Enter':
        this.onEnterPress();
        break;
      default:
        propagate = true;
        break;
    }
    if (!propagate) {
      e.stopPropagation();
    }
  }

  render() { // eslint-disable-line complexity
    const {
      item, selected, showCount, showSmallCountControls, showDescription, disabled,
      isEditable, toggleEdit, deleteItem, increment, decrement, navigateUp, navigateDown, onEnterPress, ...other
    } = this.props;

    const Icon = iconMap[item.icon];

    this.navigateUp = navigateUp;
    this.navigateDown = navigateDown;
    this.onEnterPress = onEnterPress;

    const classes = cx({
      categoryItem: true,
      selected
    });

    // `disabled` is a high-priority override
    const allowIncrement = !disabled && (item.allow_multiple || item.count === 0);
    const allowDecrement = item.count > 0;

    const minusStyle = allowDecrement ? styles.countControlAction : styles.disabledCountControlAction;
    const plusStyle = allowIncrement ? styles.countControlAction : styles.disabledCountControlAction;

    const countControlsStyle = showSmallCountControls ?
      styles.smallCountControls : styles.largeCountControls;

    const description = <div className={styles.categoryDescription}>{item.description}</div>;

    const deleteControls = (
      <div className={styles.countControlAction} onClick={(e) => {e.stopPropagation(); deleteItem();}}>
        <TrashIcon />
      </div>
    );

    const editControls = (
      <div className={styles.countControlAction} onClick={(e) => {e.stopPropagation(); toggleEdit();}}>
        <EditIcon />
      </div>
    );

    const minus = (
      <div className={minusStyle} onClick={(e) => {e.stopPropagation(); allowDecrement && decrement();}}>
        <Minus1Icon size="small" />
      </div>
    );
    const plus = (
      <div className={plusStyle} onClick={(e) => {e.stopPropagation(); allowIncrement && increment();}}>
        <Add1Icon size="small" />
      </div>
    );

    return (
      <div onKeyDown={this.handleKeyPress} {...other} tabIndex="0" className={classes}>
        <div className={styles.categoryIcon}>{Icon ? <Icon /> : 'Missing: ' + item.icon}</div>

        <div className={styles.categoryTextWrapper} onClick={() => {allowIncrement && increment();}}>
          <span className={styles.categoryText}>{item.name}</span>
          {showDescription ? description : null}
        </div>
        <div className={countControlsStyle}>
          {/* TODO: Move these controls into a new, separate component */}
          <div className={styles.countControlContainer}>
            {selected && deleteItem ? deleteControls : null}
            {selected && isEditable ? editControls : null}
            {showCount || selected ? minus : null}
            {selected || item.count > 0 ? <div className={styles.countControlChild}>{item.count}</div> : null}
            {showCount || selected ? plus : null}
          </div>
        </div>
      </div>
    );
  }
}

CategoryItem.propTypes = {
  item: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  showCount: PropTypes.bool,
  showDescription: PropTypes.bool,
  showSmallCountControls: PropTypes.bool,
  increment: PropTypes.func,
  decrement: PropTypes.func,
  navigateUp: PropTypes.func,
  navigateDown: PropTypes.func,
  onEnterPress: PropTypes.func,
  toggleEdit: PropTypes.func,
  isEditable: PropTypes.bool,
  deleteItem: PropTypes.func
};

CategoryItem.defaultPropTypes = {
  showSmallCountControls: false,
  disabled: false,
};
CategoryItem.defaultProps = {
  navigateUp: () => undefined,
  navigateDown: () => undefined,
  onEnterPress: () => undefined,
};
